import { State } from './state.model';
import { PromiseState, StatefulPromise } from '../../util/stateful-promise';
/**
 * Default state mapper function (maps promises that are fulfilled with something other than null or undefined to the
 * Content state).
 * @param {StatefulPromise<any>} promise Stateful promise
 * @returns {State} Component state
 */
export var DEFAULT_STATE_MAPPER = function (promise) {
    if (!promise) {
        return State.Empty;
    }
    switch (promise.state) {
        case PromiseState.Pending:
            return State.Loading;
        case PromiseState.Fulfilled:
            return (promise.value === null || promise.value === undefined) ? State.Empty : State.Content;
        case PromiseState.Rejected:
            return State.Error;
    }
};
/**
 * Wrapper component that has empty, loading, content, and error states. Only the proper child view will be shown
 * depending on the state. The component may be given a state directly, or a StatefulPromise to track.
 */
var StateViewComponent = /** @class */ (function () {
    function StateViewComponent() {
        this.State = State;
        /**
         * Component state. When this property is updated, the view will be updated to reflect the new state.
         */
        this.state = State.Empty;
        /**
         * Optional state mapping function. By default, the following logic is used:
         * (1) If the promise is undefined, it will be mapped to the "Empty" state.
         * (2) If the promise is not fulfilled (neither resolved nor rejected), it will be mapped to the "Pending" state.
         * (3) If the promise resolves to a value other than null or undefined, it will be mapped to the "Content" state.
         * (4) If the promise resolves to either null or undefined, it will be mapped to the "Empty" state.
         * (5) If the promise is rejected, it will be mapped to the "Error" state.
         */
        this.mapState = DEFAULT_STATE_MAPPER;
    }
    Object.defineProperty(StateViewComponent.prototype, "promise", {
        /**
         * Stateful promise from which the state will be derived. This may be used in lieu of the "state" property. When
         * the promise is fulfilled or rejected, the component state will be updated based on the promise state.
         */
        set: function (promise) {
            var _this = this;
            this._promise = promise;
            this._updateState();
            if (promise && promise.state === PromiseState.Pending) {
                this._promise.promise.then(function () { return _this._updateState(); }, function () { return _this._updateState(); });
            }
        },
        enumerable: true,
        configurable: true
    });
    StateViewComponent.prototype._updateState = function () {
        this.state = this.mapState(this._promise);
    };
    return StateViewComponent;
}());
export { StateViewComponent };
